import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { 
  AlertCircle, 
  Phone, 
  DollarSign, 
  Clock, 
  CheckCircle,
  Bell,
  Calendar,
  Banknote,
  CreditCard,
  RefreshCw,
  Undo2,
  Trash2
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Debts = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [debts, setDebts] = useState([]);
  const [reminders, setReminders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending');
  const [showPayModal, setShowPayModal] = useState(false);
  const [selectedDebt, setSelectedDebt] = useState(null);
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('efectivo');
  const [showUndoModal, setShowUndoModal] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [undoLoading, setUndoLoading] = useState(false);

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  const fetchData = async () => {
    try {
      const [debtsRes, remindersRes] = await Promise.all([
        axios.get(`${API}/debts`, { params: { status: filter === 'all' ? null : filter } }),
        axios.get(`${API}/debts/reminders`)
      ]);
      setDebts(debtsRes.data);
      setReminders(remindersRes.data);
    } catch (error) {
      toast.error('Error al cargar deudas');
    } finally {
      setLoading(false);
    }
  };

  const handlePayment = async () => {
    if (!paymentAmount || parseFloat(paymentAmount) <= 0) {
      toast.error('Ingresa un monto válido');
      return;
    }

    try {
      await axios.post(`${API}/debts/${selectedDebt.id}/pay`, {
        amount: parseFloat(paymentAmount),
        method: paymentMethod  // Backend expects 'method', not 'payment_method'
      });
      toast.success(`Pago de ${formatCurrency(paymentAmount)} registrado (${paymentMethod})`);
      setShowPayModal(false);
      setSelectedDebt(null);
      setPaymentAmount('');
      setPaymentMethod('efectivo');
      fetchData();
    } catch (error) {
      toast.error('Error al registrar pago');
    }
  };

  const openPayModal = (debt) => {
    setSelectedDebt(debt);
    setPaymentAmount(debt.remaining_amount?.toString() || debt.amount.toString());
    setPaymentMethod('efectivo');
    setShowPayModal(true);
  };

  // Undo payment function (admin only)
  const handleUndoPayment = async () => {
    if (!selectedPayment) return;
    
    setUndoLoading(true);
    try {
      await axios.delete(`${API}/debt-payments/${selectedPayment.id}`);
      toast.success('Pago deshecho correctamente. La deuda ha sido restaurada.');
      setShowUndoModal(false);
      setSelectedPayment(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al deshacer el pago');
    } finally {
      setUndoLoading(false);
    }
  };

  const openUndoModal = async (debt) => {
    // Fetch payments for this specific debt
    try {
      const response = await axios.get(`${API}/debts/${debt.id}/payments`);
      const payments = response.data || [];
      // Get the most recent payment (first one since sorted by date desc)
      const lastPayment = payments[0];
      
      if (lastPayment) {
        setSelectedPayment({ ...lastPayment, debt_customer: debt.customer_name });
        setShowUndoModal(true);
      } else {
        toast.error('No se encontró el pago asociado a esta deuda');
      }
    } catch (error) {
      toast.error('Error al buscar el pago');
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 0
    }).format(value);
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#FF3B30]" />
      </div>
    );
  }

  const urgentReminders = reminders.filter(r => r.days_until <= 3);

  return (
    <div data-testid="debts-page" className="space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-black text-white">Deudas y Cobros</h1>
          <p className="text-zinc-500 text-sm">Gestiona las deudas pendientes</p>
        </div>
        <Button
          onClick={fetchData}
          variant="outline"
          size="sm"
          className="border-zinc-700 text-zinc-300 hover:bg-zinc-800"
          disabled={loading}
        >
          <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
        </Button>
      </div>

      {/* Reminders Alert */}
      {urgentReminders.length > 0 && (
        <Card className="bg-amber-600/10 border-amber-600/30">
          <CardContent className="p-3 sm:p-4">
            <div className="flex items-start gap-3">
              <Bell className="text-amber-500 flex-shrink-0 mt-0.5" size={18} />
              <div className="flex-1 min-w-0">
                <h3 className="font-semibold text-amber-500 mb-2 text-sm sm:text-base">Recordatorios de Cobro</h3>
                <div className="space-y-2">
                  {urgentReminders.slice(0, 5).map((reminder, idx) => (
                    <div key={idx} className="flex flex-wrap items-center gap-2 text-xs sm:text-sm">
                      <span className={`px-2 py-0.5 rounded text-[10px] sm:text-xs font-medium ${
                        reminder.days_pending > 7 
                          ? 'bg-red-500/20 text-red-400' 
                          : 'bg-amber-500/20 text-amber-400'
                      }`}>
                        {reminder.days_pending} {reminder.days_pending === 1 ? 'día' : 'días'}
                      </span>
                      <span className="text-white truncate">{reminder.customer_name}</span>
                      <span className="font-mono text-amber-400">{formatCurrency(reminder.amount)}</span>
                      {reminder.customer_phone && (
                        <a 
                          href={`tel:${reminder.customer_phone}`}
                          className="text-zinc-500 hover:text-white"
                        >
                          <Phone size={12} />
                        </a>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Filter Tabs */}
      <div className="flex gap-2 overflow-x-auto pb-2">
        {[
          { value: 'pending', label: 'Pendientes' },
          { value: 'paid', label: 'Pagadas' },
          { value: 'all', label: 'Todas' }
        ].map((tab) => (
          <button
            key={tab.value}
            data-testid={`filter-${tab.value}`}
            onClick={() => setFilter(tab.value)}
            className={`px-3 sm:px-4 py-2 rounded-sm text-xs sm:text-sm font-medium transition-fast whitespace-nowrap ${
              filter === tab.value
                ? 'bg-[#FF3B30] text-white'
                : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Debts List */}
      <div className="space-y-3">
        {debts.map((debt) => (
          <Card
            key={debt.id}
            data-testid={`debt-${debt.id}`}
            className={`bg-[#0A0A0A] border-zinc-800 ${
              debt.status === 'paid' ? 'opacity-60' : ''
            }`}
          >
            <CardContent className="p-3 sm:p-4">
              <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-sm flex items-center justify-center flex-shrink-0 ${
                    debt.status === 'paid' 
                      ? 'bg-emerald-600/20'
                      : debt.status === 'partial'
                      ? 'bg-sky-600/20'
                      : 'bg-amber-600/20'
                  }`}>
                    {debt.status === 'paid' 
                      ? <CheckCircle className="text-emerald-500" size={20} />
                      : debt.status === 'partial'
                      ? <DollarSign className="text-sky-400" size={20} />
                      : <Clock className="text-amber-500" size={20} />
                    }
                  </div>
                  <div className="min-w-0 flex-1">
                    <h3 className="font-semibold text-white text-sm sm:text-base truncate">{debt.customer_name}</h3>
                    <div className="flex items-center gap-2 mt-0.5">
                      <span className="text-[10px] sm:text-xs text-zinc-500">
                        <Calendar size={10} className="inline mr-1" />
                        {formatDate(debt.created_at)}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="flex items-center justify-between sm:justify-end gap-3 sm:gap-4">
                  <div className="text-left sm:text-right">
                    <p className="text-lg sm:text-2xl font-mono font-bold text-white">
                      {formatCurrency(debt.remaining_amount || debt.amount)}
                    </p>
                    {debt.remaining_amount && debt.remaining_amount !== debt.amount && (
                      <p className="text-[10px] sm:text-xs text-zinc-500">
                        de {formatCurrency(debt.amount)}
                      </p>
                    )}
                  </div>
                  
                  <Badge 
                    variant={debt.status === 'paid' ? 'default' : 'secondary'}
                    className={`text-[10px] sm:text-xs ${
                      debt.status === 'paid'
                        ? 'bg-emerald-600/20 text-emerald-400 border-emerald-600/30'
                        : debt.status === 'partial'
                        ? 'bg-sky-600/20 text-sky-400 border-sky-600/30'
                        : 'bg-amber-600/20 text-amber-400 border-amber-600/30'
                    }`}
                  >
                    {debt.status === 'paid'
                      ? 'Pagado'
                      : debt.status === 'partial'
                      ? 'Parcial'
                      : 'Pendiente'}
                  </Badge>

                  {(debt.status === 'pending' || debt.status === 'partial') && (
                    <Button
                      data-testid={`pay-debt-${debt.id}`}
                      onClick={() => openPayModal(debt)}
                      size="sm"
                      className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs sm:text-sm"
                    >
                      <DollarSign size={14} className="mr-1" />
                      Cobrar
                    </Button>
                  )}
                </div>
              </div>

              {/* Payment info if paid */}
              {debt.status === 'paid' && (
                <div className="mt-3 pt-3 border-t border-zinc-800 flex items-center justify-between">
                  <div className="text-xs text-zinc-500">
                    Pagado con: <span className="text-zinc-300">{debt.paid_method || 'N/A'}</span>
                  </div>
                  {isAdmin && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => openUndoModal(debt)}
                      className="border-amber-600/50 text-amber-400 hover:bg-amber-600/10 text-xs"
                    >
                      <Undo2 size={12} className="mr-1" />
                      Deshacer Pago
                    </Button>
                  )}
                </div>
              )}

              {debt.notes && (
                <p className="text-xs sm:text-sm text-zinc-500 mt-3 pt-3 border-t border-zinc-800">
                  {debt.notes}
                </p>
              )}

              {/* Reminder dates */}
              {debt.status === 'pending' && debt.reminder_dates?.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t border-zinc-800">
                  <span className="text-[10px] sm:text-xs text-zinc-500">Recordatorios:</span>
                  {debt.reminder_dates.slice(0, 3).map((date, idx) => (
                    <span key={idx} className="text-[10px] sm:text-xs bg-zinc-800 px-2 py-0.5 rounded">
                      {formatDate(date)}
                    </span>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      {debts.length === 0 && (
        <div className="text-center py-12">
          <AlertCircle size={48} className="mx-auto text-zinc-700 mb-4" />
          <p className="text-zinc-500">No hay deudas {filter === 'pending' ? 'pendientes' : ''}</p>
        </div>
      )}

      {/* Payment Modal */}
      <Dialog open={showPayModal} onOpenChange={setShowPayModal}>
        <DialogContent className="bg-[#0A0A0A] border-zinc-800 max-w-sm mx-4">
          <DialogHeader>
            <DialogTitle className="text-white">Registrar Pago</DialogTitle>
          </DialogHeader>
          {selectedDebt && (
            <div className="space-y-4">
              <div className="bg-zinc-900 p-4 rounded-sm">
                <p className="text-sm text-zinc-400">Cliente</p>
                <p className="text-lg font-semibold text-white">{selectedDebt.customer_name}</p>
                <p className="text-sm text-zinc-400 mt-2">Deuda pendiente</p>
                <p className="text-2xl font-mono font-bold text-[#FF3B30]">
                  {formatCurrency(selectedDebt.remaining_amount || selectedDebt.amount)}
                </p>
              </div>
              
              <div>
                <Label className="text-sm text-zinc-400 mb-2 block">Monto a pagar</Label>
                <Input
                  data-testid="payment-amount-input"
                  type="number"
                  step="0.01"
                  value={paymentAmount}
                  onChange={(e) => setPaymentAmount(e.target.value)}
                  className="bg-zinc-900 border-zinc-800 text-lg font-mono"
                />
              </div>

              <div>
                <Label className="text-sm text-zinc-400 mb-2 block">Método de Pago</Label>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    data-testid="payment-method-efectivo"
                    onClick={() => setPaymentMethod('efectivo')}
                    className={`flex items-center justify-center gap-2 py-3 rounded-sm font-medium transition-fast ${
                      paymentMethod === 'efectivo'
                        ? 'bg-emerald-600 text-white'
                        : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'
                    }`}
                  >
                    <Banknote size={18} />
                    Efectivo
                  </button>
                  <button
                    data-testid="payment-method-tarjeta"
                    onClick={() => setPaymentMethod('tarjeta')}
                    className={`flex items-center justify-center gap-2 py-3 rounded-sm font-medium transition-fast ${
                      paymentMethod === 'tarjeta'
                        ? 'bg-blue-600 text-white'
                        : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'
                    }`}
                  >
                    <CreditCard size={18} />
                    Tarjeta
                  </button>
                </div>
              </div>
            </div>
          )}
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button
              variant="outline"
              onClick={() => setShowPayModal(false)}
              className="border-zinc-700 w-full sm:w-auto"
            >
              Cancelar
            </Button>
            <Button
              data-testid="confirm-payment-btn"
              onClick={handlePayment}
              className="bg-emerald-600 hover:bg-emerald-700 w-full sm:w-auto"
            >
              Confirmar Pago
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Undo Payment Modal (Admin only) */}
      <Dialog open={showUndoModal} onOpenChange={setShowUndoModal}>
        <DialogContent className="bg-[#0A0A0A] border-zinc-800 max-w-sm mx-4">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Undo2 className="text-amber-400" size={20} />
              Deshacer Pago
            </DialogTitle>
          </DialogHeader>
          {selectedPayment && (
            <div className="space-y-4">
              <div className="bg-zinc-900 p-4 rounded-sm">
                <p className="text-sm text-zinc-400">Cliente</p>
                <p className="text-lg font-semibold text-white">{selectedPayment.debt_customer}</p>
                <p className="text-sm text-zinc-400 mt-2">Pago a deshacer</p>
                <p className="text-2xl font-mono font-bold text-amber-400">
                  {formatCurrency(selectedPayment.amount)}
                </p>
                <p className="text-xs text-zinc-500 mt-1">
                  Método: {selectedPayment.method === 'tarjeta' ? 'Tarjeta' : 'Efectivo'}
                </p>
              </div>
              
              <div className="bg-amber-900/20 border border-amber-800/50 p-3 rounded-sm">
                <p className="text-sm text-amber-300">
                  ⚠️ Esta acción restaurará la deuda al estado pendiente y eliminará el registro del pago.
                </p>
              </div>
            </div>
          )}
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button
              variant="outline"
              onClick={() => setShowUndoModal(false)}
              className="border-zinc-700 w-full sm:w-auto"
            >
              Cancelar
            </Button>
            <Button
              onClick={handleUndoPayment}
              disabled={undoLoading}
              className="bg-amber-600 hover:bg-amber-700 w-full sm:w-auto"
            >
              {undoLoading ? (
                <>
                  <RefreshCw size={14} className="mr-2 animate-spin" />
                  Procesando...
                </>
              ) : (
                <>
                  <Trash2 size={14} className="mr-2" />
                  Confirmar Deshacer
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Debts;
